import { useEffect, useState } from 'react';
import { parseCTe, isCTe, isNFe } from '../utils/parseCTe';
import { Eye, Edit, Rocket, Trash2, DollarSign } from 'lucide-react';
import { getPaymentMethodName } from '../utils/paymentMethods';
import {
  Upload,
  FileText,
  Type,
  Building2,
  Hash,
  Calendar,
  AlertTriangle,
  X,
  Info,
  Plus,
  ClipboardList,
  CreditCard,
  Truck,
} from 'lucide-react';
import { api } from '../services/api';

const initialManualForm = {
  numero: '',
  data_emissao: '',
  valor_total: '',
  tipo_operacao: 'pagar',
  emitente_nome: '',
  emitente_cnpj: '',
  destinatario_nome: '',
  destinatario_cnpj: '',
  observacoes: '',
  data_vencimento: ''
};

const sanitizeCnpj = (value) => (value ? value.replace(/\D/g, '') : '');

const formatCnpj = (value) => {
  const digits = sanitizeCnpj(value);
  if (digits.length !== 14) return value || '';
  return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

const parseCurrencyString = (value) => {
  if (typeof value === 'number') return value;
  if (value === null || value === undefined) return 0;

  const raw = value.toString().trim();
  if (!raw) return 0;

  const cleaned = raw.replace(/\s+/g, '').replace(/[^0-9,.-]/g, '');
  const commaIndex = cleaned.lastIndexOf(',');
  const dotIndex = cleaned.lastIndexOf('.');

  if (commaIndex > -1 && dotIndex > -1) {
    if (dotIndex < commaIndex) {
      const semPontos = cleaned.replace(/\./g, '');
      return parseFloat(semPontos.replace(',', '.')) || 0;
    }
    const semVirgulas = cleaned.replace(/,/g, '');
    return parseFloat(semVirgulas) || 0;
  }

  if (commaIndex > -1) {
    return parseFloat(cleaned.replace(/\./g, '').replace(',', '.')) || 0;
  }

  if ((cleaned.match(/\./g) || []).length > 1) {
    return parseFloat(cleaned.replace(/\./g, '')) || 0;
  }

  return parseFloat(cleaned) || 0;
};

const formatCurrency = (value) => {
  const number = typeof value === 'number' ? value : parseCurrencyString(value);
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(Number.isFinite(number) ? number : 0);
};

const formatDate = (date) => {
  if (!date) return '';
  try {
    return new Date(date + 'T00:00:00').toLocaleDateString('pt-BR');
  } catch (error) {
    return date;
  }
};

const parseBrazilianDate = (value) => {
  if (!value) return '';
  const parts = value.split(/[\/]/);
  if (parts.length === 3) {
    const [day, month, year] = parts;
    if (day.length === 2 && month.length === 2 && year.length === 4) {
      return `${year}-${month}-${day}`;
    }
  }
  return value;
};

const inferTipoOperacao = (emitenteCnpj, destinatarioCnpj, empresaCnpj) => {
  const empresa = sanitizeCnpj(empresaCnpj);
  if (!empresa) return null;
  if (empresa && sanitizeCnpj(emitenteCnpj) === empresa) return 'receber';
  if (empresa && sanitizeCnpj(destinatarioCnpj) === empresa) return 'pagar';
  return null;
};

const buildMetadataFromNota = (nota) => {
  if (!nota) return null;
  return {
    numero: nota.numero || '',
    valor_total: nota.valor_total || 0,
    data_emissao: nota.data_emissao || '',
    data_vencimento: nota.data_vencimento || '',
    tipo_operacao: nota.tipo_operacao || null,
    emitente: nota.emitente || null,
    destinatario: nota.destinatario || null,
    parcelas: Array.isArray(nota.parcelas) ? nota.parcelas : [],
    pagamentos: Array.isArray(nota.pagamentos) ? nota.pagamentos : [],
    faturas: Array.isArray(nota.faturas) ? nota.faturas : [],
    chave_acesso: nota.chave_acesso || null,
    origem: nota.origem || null,
    observacoes: nota.observacoes || ''
  };
};

const findDuplicateNota = (lista, referencia, ignoreId = null) => {
  if (!Array.isArray(lista) || !referencia) return null;

  const numeroReferencia = referencia.numero ? referencia.numero.toString().trim() : '';
  const chaveReferencia = referencia.chave_acesso ? referencia.chave_acesso.toString().trim() : '';

  if (!numeroReferencia && !chaveReferencia) {
    return null;
  }

  return (
    lista.find((nota) => {
      if (!nota) return false;
      if (ignoreId && (nota.id === ignoreId || nota.numero === ignoreId)) {
        return false;
      }
      const numeroNota = nota.numero ? nota.numero.toString().trim() : '';
      const chaveNota = nota.chave_acesso ? nota.chave_acesso.toString().trim() : '';
      return (
        (numeroReferencia && numeroNota && numeroNota === numeroReferencia) ||
        (chaveReferencia && chaveNota && chaveNota === chaveReferencia)
      );
    }) || null
  );
};

const buildNotaPayloadForApi = (nota) => {
  if (!nota) return null;

  const parcelas = Array.isArray(nota.parcelas)
    ? nota.parcelas.map((parcela, index) => ({
        id: parcela.id,
        numero: parcela.numero ?? String(index + 1),
        data_vencimento: parcela.data_vencimento || null,
        valor: parseCurrencyString(parcela.valor),
        status: parcela.status || null
      }))
    : [];

  const faturas = Array.isArray(nota.faturas)
    ? nota.faturas.map((fatura) => ({
        id: fatura.id,
        numero: fatura.numero || null,
        valor_original: parseCurrencyString(fatura.valor_original),
        valor_desconto: parseCurrencyString(fatura.valor_desconto),
        valor_liquido: parseCurrencyString(fatura.valor_liquido)
      }))
    : [];

  const pagamentos = Array.isArray(nota.pagamentos)
    ? nota.pagamentos.map((pagamento) => ({
        id: pagamento.id,
        meio: pagamento.meio || null,
        indicador: pagamento.indicador || null,
        valor: parseCurrencyString(pagamento.valor),
        data_pagamento: pagamento.data_pagamento || null
      }))
    : [];

  const payload = {
    numero: nota.numero || '',
    serie: nota.serie || null,
    chave_acesso: nota.chave_acesso || null,
    data_emissao: nota.data_emissao || '',
    valor_total: parseCurrencyString(nota.valor_total),
    status: nota.status || null,
    tipo_operacao: nota.tipo_operacao || null,
    data_vencimento: nota.data_vencimento || null,
    origem: nota.origem || null,
    observacoes: nota.observacoes || '',
    parcelas,
    faturas,
    pagamentos,
    empresa_id: nota.empresa_id || null,
    metadata: {
      emitente: nota.emitente || null,
      destinatario: nota.destinatario || null,
      parcelas,
      faturas,
      pagamentos,
      chave_acesso: nota.chave_acesso || null,
      tipo_operacao: nota.tipo_operacao || null,
      data_vencimento: nota.data_vencimento || null,
      observacoes: nota.observacoes || ''
    }
  };

  return payload;
};

const mergeNotaWithMetadata = (nota, metadata) => {
  if (!metadata) {
    return {
      ...nota,
      parcelas: Array.isArray(nota.parcelas) ? nota.parcelas : [],
      pagamentos: Array.isArray(nota.pagamentos) ? nota.pagamentos : [],
      faturas: Array.isArray(nota.faturas) ? nota.faturas : []
    };
  }

  return {
    ...nota,
    numero: metadata.numero || nota.numero || '',
    valor_total: metadata.valor_total ?? nota.valor_total ?? 0,
    data_emissao: metadata.data_emissao || nota.data_emissao || '',
    data_vencimento: metadata.data_vencimento || nota.data_vencimento || '',
    tipo_operacao: metadata.tipo_operacao || nota.tipo_operacao || null,
    emitente: metadata.emitente || nota.emitente || null,
    destinatario: metadata.destinatario || nota.destinatario || null,
    parcelas: Array.isArray(metadata.parcelas) && metadata.parcelas.length > 0
      ? metadata.parcelas
      : Array.isArray(nota.parcelas)
        ? nota.parcelas
        : [],
    pagamentos: Array.isArray(metadata.pagamentos) ? metadata.pagamentos : nota.pagamentos || [],
    faturas: Array.isArray(metadata.faturas) ? metadata.faturas : nota.faturas || [],
    chave_acesso: metadata.chave_acesso || nota.chave_acesso || null,
    origem: metadata.origem || nota.origem || null,
    observacoes: metadata.observacoes || nota.observacoes || ''
  };
};

const normalizeNotaFromApi = (nota) => ({
  id: nota.id,
  empresa_id: nota.empresa_id ?? null,
  numero: nota.numero || '',
  fornecedor: nota.fornecedor || '',
  valor_total: nota.valor_total || 0,
  data_emissao: nota.data_emissao || '',
  data_vencimento: nota.data_vencimento || '',
  status: nota.status || 'Pendente',
  tipo_operacao: nota.tipo_operacao || null,
  parcelas: Array.isArray(nota.parcelas) ? nota.parcelas : [],
  pagamentos: Array.isArray(nota.pagamentos) ? nota.pagamentos : [],
  faturas: Array.isArray(nota.faturas) ? nota.faturas : [],
  emitente: nota.emitente || null,
  destinatario: nota.destinatario || null,
  chave_acesso: nota.chave_acesso || null,
  origem: nota.origem || null,
  observacoes: nota.observacoes || ''
});

const parseXmlNota = (xmlText, empresaCnpj) => {
  const parser = new DOMParser();
  const xml = parser.parseFromString(xmlText, 'application/xml');
  const errorNode = xml.querySelector('parsererror');
  if (errorNode) {
    throw new Error('Arquivo XML inválido.');
  }

  // ADICIONE ESTA VERIFICAÇÃO PARA CTe
  if (isCTe(xml)) {
    console.log('Detectado CTe, usando parser específico');
    const dadosCTe = parseCTe(xml);
    
    // Adaptar dados do CTe para o formato esperado pelo sistema
    return {
      numero: dadosCTe.numero,
      data_emissao: dadosCTe.data_emissao,
      valor_total: parseFloat(dadosCTe.valor_total),
      tipo_operacao: 'pagar', // CTe sempre é despesa
      
      // Para CTe, o emitente é a transportadora
      emitente: {
        nome: dadosCTe.transportadora?.nome || dadosCTe.fornecedor_cliente,
        fantasia: dadosCTe.transportadora?.fantasia,
        cnpj: dadosCTe.transportadora?.cnpj || dadosCTe.cnpj_cpf,
        ie: dadosCTe.transportadora?.ie || dadosCTe.inscricao_estadual
      },
      
      // Destinatário do frete
      destinatario: dadosCTe.destinatario ? {
        nome: dadosCTe.destinatario.nome,
        cnpj: dadosCTe.destinatario.cnpj
      } : null,
      
      // Remetente (adicional para CTe)
      remetente: dadosCTe.remetente ? {
        nome: dadosCTe.remetente.nome,
        cnpj: dadosCTe.remetente.cnpj
      } : null,
      
      parcelas: dadosCTe.data_vencimento ? [{
        numero: '1',
        data_vencimento: dadosCTe.data_vencimento,
        valor: parseFloat(dadosCTe.valor_total)
      }] : [],
      
      faturas: [],
      pagamentos: [],
      chave_acesso: dadosCTe.chave_acesso,
      origem: 'xml_cte',
      status: 'Importada',
      observacoes: dadosCTe.observacoes || '',
      data_vencimento: dadosCTe.data_vencimento || '',
      
      // Metadados específicos do CTe
      tipo_documento: 'CTe',
      fornecedor: dadosCTe.fornecedor_cliente, // Nome da transportadora
      produto: dadosCTe.produto,
      valor_carga: dadosCTe.valor_carga,
      componentes_valor: dadosCTe.componentes_valor,
      chave_nfe_relacionada: dadosCTe.chave_nfe_relacionada,
      
      // Visual
      icone_tipo: '🚚',
      badge_tipo: 'CT-e',
      cor_badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
    };
  }

  const infNFe = xml.getElementsByTagName('infNFe')[0];
  if (!infNFe) {
    throw new Error('Estrutura da NF-e não identificada.');
  }

  const getTagValue = (parent, tag) => {
    if (!parent) return '';
    const element = parent.getElementsByTagName(tag)[0];
    return element ? element.textContent.trim() : '';
  };

  const emitenteNode = infNFe.getElementsByTagName('emit')[0];
  const destinatarioNode = infNFe.getElementsByTagName('dest')[0];
  const totalNode = infNFe.getElementsByTagName('total')[0];
  const cobrNode = infNFe.getElementsByTagName('cobr')[0];
  const pagNode = infNFe.getElementsByTagName('pag')[0];
  const ideNode = infNFe.getElementsByTagName('ide')[0];

  const emitente = emitenteNode
    ? {
        nome: getTagValue(emitenteNode, 'xNome'),
        fantasia: getTagValue(emitenteNode, 'xFant'),
        cnpj: getTagValue(emitenteNode, 'CNPJ') || getTagValue(emitenteNode, 'CPF'),
        ie: getTagValue(emitenteNode, 'IE'),
        endereco: {
          logradouro: getTagValue(emitenteNode, 'xLgr'),
          numero: getTagValue(emitenteNode, 'nro'),
          bairro: getTagValue(emitenteNode, 'xBairro'),
          municipio: getTagValue(emitenteNode, 'xMun'),
          uf: getTagValue(emitenteNode, 'UF')
        }
      }
    : null;

  const destinatario = destinatarioNode
    ? {
        nome: getTagValue(destinatarioNode, 'xNome'),
        fantasia: getTagValue(destinatarioNode, 'xFant'),
        cnpj: getTagValue(destinatarioNode, 'CNPJ') || getTagValue(destinatarioNode, 'CPF'),
        ie: getTagValue(destinatarioNode, 'IE'),
        endereco: {
          logradouro: getTagValue(destinatarioNode, 'xLgr'),
          numero: getTagValue(destinatarioNode, 'nro'),
          bairro: getTagValue(destinatarioNode, 'xBairro'),
          municipio: getTagValue(destinatarioNode, 'xMun'),
          uf: getTagValue(destinatarioNode, 'UF')
        }
      }
    : null;

  const parcelas = [];
  if (cobrNode) {
    const duplicatas = Array.from(cobrNode.getElementsByTagName('dup'));
    duplicatas.forEach((dup, index) => {
      parcelas.push({
        numero: getTagValue(dup, 'nDup') || String(index + 1),
        data_vencimento: getTagValue(dup, 'dVenc'),
        valor: parseCurrencyString(getTagValue(dup, 'vDup'))
      });
    });
  }

  const faturas = [];
  if (cobrNode) {
    const fatNodes = Array.from(cobrNode.getElementsByTagName('fat'));
    fatNodes.forEach((fat) => {
      faturas.push({
        numero: getTagValue(fat, 'nFat'),
        valor_original: parseCurrencyString(getTagValue(fat, 'vOrig')),
        valor_desconto: parseCurrencyString(getTagValue(fat, 'vDesc')),
        valor_liquido: parseCurrencyString(getTagValue(fat, 'vLiq'))
      });
    });
  }

  const pagamentos = [];
  if (pagNode) {
    const detPagNodes = Array.from(pagNode.getElementsByTagName('detPag'));
    detPagNodes.forEach((pag) => {
      pagamentos.push({
        meio: getTagValue(pag, 'tPag'),
        valor: parseCurrencyString(getTagValue(pag, 'vPag')),
        indicador: getTagValue(pag, 'indPag')
      });
    });
  }

  const numero = getTagValue(ideNode, 'nNF');
  const dataEmissao = getTagValue(ideNode, 'dhEmi') || getTagValue(ideNode, 'dEmi');
  const chave = infNFe.getAttribute('Id') ? infNFe.getAttribute('Id').replace(/^NFe/i, '') : '';
  const valorTotal = parseCurrencyString(getTagValue(totalNode, 'vNF'));

  const tipo = inferTipoOperacao(emitente?.cnpj, destinatario?.cnpj, empresaCnpj);

  return {
    numero,
    data_emissao: dataEmissao ? dataEmissao.substring(0, 10) : '',
    valor_total: valorTotal,
    tipo_operacao: tipo,
    emitente,
    destinatario,
    parcelas,
    faturas,
    pagamentos,
    chave_acesso: chave,
    origem: 'xml',
    status: 'Importada',
    observacoes: '',
    data_vencimento: parcelas.length === 0 ? getTagValue(cobrNode, 'dVenc') || '' : ''
  };
};

const getTipoOperacaoBadge = (tipo) => {
  if (tipo === 'pagar') {
    return 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200';
  }
  if (tipo === 'receber') {
    return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200';
  }
  return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300';
};

const tipoOperacaoLabel = (tipo) => {
  if (tipo === 'pagar') return 'Conta a pagar';
  if (tipo === 'receber') return 'Conta a receber';
  return 'Tipo não identificado';
};

const EmpresaInfoFallback = { id: null, nome: '', cnpj: '', email: '', telefone: '', endereco: '' };

const mapEmpresaInfo = (data) => ({
  id: data?.id ?? null,
  nome: data?.nome || data?.razao_social || '',
  cnpj: data?.cnpj || '',
  email: data?.email || '',
  telefone: data?.telefone || '',
  endereco: data?.endereco || ''
});

// ————————————————————————————————————————————————
// Helpers de formatação / resumo de parcelas
function getParcelasInfo(nota) {
  const lista = Array.isArray(nota.parcelas) ? nota.parcelas : [];
  if (lista.length > 0) {
    // Normaliza e ordena por data
    const norm = lista.map((p, i) => ({
      numero: p.numero ?? i + 1,
      data_vencimento: p.data_vencimento || null,
      valor: Number.isFinite(p.valor) ? p.valor : 0
    }));
    norm.sort((a, b) => (a.data_vencimento || '').localeCompare(b.data_vencimento || ''));
    const total = norm.reduce((acc, p) => acc + (p.valor || 0), 0);
    const proxima = norm.find(p => p.data_vencimento);
    return { kind: norm.length === 1 ? 'single' : 'multi', list: norm, total, next: proxima || null };
  }

  // fallback: “vencimento único” via campo da nota
  if (nota.data_vencimento) {
    return {
      kind: 'single',
      list: [{ numero: 1, data_vencimento: nota.data_vencimento, valor: Number(nota.valor_total) || 0 }],
      total: Number(nota.valor_total) || 0,
      next: { data_vencimento: nota.data_vencimento }
    };
  }

  return { kind: 'none', list: [], total: 0, next: null };
}

function Badge({ children, tone = 'neutral' }) {
  const tones = {
    neutral: 'bg-gray-100 text-gray-700 dark:bg-gray-800/70 dark:text-gray-200',
    green: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200',
    blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200'
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${tones[tone]}`}>
      {children}
    </span>
  );
}

function Pill({ date, value }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-gray-300/60 dark:border-gray-700 px-2.5 py-1 text-xs">
      <span className="text-gray-600 dark:text-gray-300">{date}</span>
      <span className="font-mono tabular-nums font-semibold text-primary-600 dark:text-primary-400">{value}</span>
    </span>
  );
}

function ParcelasCell({ nota, formatDate, formatCurrency, compact = false }) {
  const listaBruta = Array.isArray(nota.parcelas) ? nota.parcelas : [];
  const lista = listaBruta.length
    ? listaBruta.map((p, i) => ({
        numero: p.numero ?? i + 1,
        data_vencimento: p.data_vencimento || null,
        valor: Number.isFinite(p.valor) ? p.valor : Number(nota.valor_total) || 0,
      }))
    : (nota.data_vencimento
        ? [{ numero: 1, data_vencimento: nota.data_vencimento, valor: Number(nota.valor_total) || 0 }]
        : []);

  let headerText = 'Sem parcelas';
  let headerTone = 'bg-gray-100 text-gray-700 dark:bg-gray-800/70 dark:text-gray-200';
  if (lista.length === 1) {
    headerText = 'Parcela única';
    headerTone = 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200';
  }
  if (lista.length > 1) {
    headerText = `${lista.length} parcelas`;
    headerTone = 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200';
  }

  // tamanhos “apertados”
  const wrapW = compact ? 'w-[320px] md:w-[360px]' : 'w-[520px]';
  const pad    = compact ? 'px-3 py-2.5' : 'px-4 py-3';
  const gapX   = compact ? 'gap-x-3' : 'gap-x-8';
  const valCls = compact
    ? 'text-center font-mono tabular-nums font-semibold text-sm md:text-base text-primary-600 dark:text-primary-400'
    : 'text-center font-mono tabular-nums font-semibold text-primary-600 dark:text-primary-400';
  const dateCls = compact
    ? 'text-center text-xs md:text-sm text-gray-700 dark:text-gray-200'
    : 'text-center text-sm text-gray-700 dark:text-gray-200';

  return (
    <div className="flex justify-center">
      <div className={`${wrapW} max-w-full`}>
        <div className="rounded-xl border border-gray-300/30 dark:border-gray-700 bg-gray-50/40 dark:bg-gray-800/30 relative">
          <div className="flex justify-center">
            <div className={`-mt-3 px-3 py-1 rounded-full text-[11px] font-semibold ${headerTone}`}>
              {headerText}
            </div>
          </div>

          <div className={pad}>
            {lista.length === 0 ? (
              <div className={`grid grid-cols-2 ${gapX}`}>
                <div className="text-center text-sm text-gray-500 dark:text-gray-400">—</div>
                <div className="text-center text-sm text-gray-500 dark:text-gray-400">—</div>
              </div>
            ) : (
              <>
                <div className={`grid grid-cols-2 ${gapX} mb-1 text-[10px] md:text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400`}>
                  <div className="text-center">Valor</div>
                  <div className="text-center">Vencimento</div>
                </div>

                <div className={`grid grid-cols-2 ${gapX}`}>
                  <div className="space-y-1">
                    {lista.map((p, idx) => (
                      <div key={`v-${idx}`} className={valCls}>
                        {formatCurrency(p.valor)}
                      </div>
                    ))}
                  </div>
                  <div className="space-y-1">
                    {lista.map((p, idx) => (
                      <div key={`d-${idx}`} className={dateCls}>
                        {p.data_vencimento ? formatDate(p.data_vencimento) : 'Sem vencimento'}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}



export default function NotasFiscais() {
  const [notas, setNotas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedNotaId, setSelectedNotaId] = useState(null);
  const [selectedNotaState, setSelectedNotaState] = useState(null);
  const [empresaInfo, setEmpresaInfo] = useState(EmpresaInfoFallback);
  const [importProcessing, setImportProcessing] = useState(false);
  const [importError, setImportError] = useState('');
  const [importedNota, setImportedNota] = useState(null);
  const [formData, setFormData] = useState(initialManualForm);
  const [formParcelas, setFormParcelas] = useState([]);
  const [generatingLancamentoId, setGeneratingLancamentoId] = useState(null);
  const [selectedNotaDirty, setSelectedNotaDirty] = useState(false);
  const [savingNotaId, setSavingNotaId] = useState(null);
  const savingSelectedNota =
    selectedNotaState?.id != null && savingNotaId != null
      ? String(savingNotaId) === String(selectedNotaState.id)
      : false;
  const canPersistSelectedNota = selectedNotaState
    ? selectedNotaState.id
      ? selectedNotaDirty && !savingSelectedNota
      : selectedNotaDirty
    : false;

  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      let info = { ...EmpresaInfoFallback };

      try {
        const empresa = await api.getEmpresaConfig();
        info = { ...EmpresaInfoFallback, ...mapEmpresaInfo(empresa) };
        setEmpresaInfo(info);
      } catch (error) {
        console.error('Erro ao carregar dados da empresa para notas fiscais:', error);
        setEmpresaInfo({ ...EmpresaInfoFallback });
      }

      const sucesso = await loadNotas({ silent: true, empresaCnpj: info.cnpj });
      if (!sucesso) {
        alert('Erro ao carregar notas fiscais');
      }
      setLoading(false);
    };

    initialize();
  }, []);

  useEffect(() => {
    if (selectedNotaId) {
      const notaAtual = notas.find((nota) => nota.id === selectedNotaId || nota.numero === selectedNotaId);
      setSelectedNotaState(notaAtual ? JSON.parse(JSON.stringify(notaAtual)) : null);
      setSelectedNotaDirty(false);
    } else {
      setSelectedNotaState(null);
      setSelectedNotaDirty(false);
    }
  }, [selectedNotaId, notas]);

  useEffect(() => {
    if (!empresaInfo?.cnpj) return;
    setNotas((prev) =>
      prev.map((nota) => {
        if (nota.tipo_operacao) return nota;
        const tipo = inferTipoOperacao(nota.emitente?.cnpj, nota.destinatario?.cnpj, empresaInfo.cnpj);
        return tipo ? { ...nota, tipo_operacao: tipo } : nota;
      })
    );
  }, [empresaInfo.cnpj]);

  const loadNotas = async ({ silent = false, empresaCnpj } = {}) => {
    if (!silent) {
      setLoading(true);
    }

    try {
      const data = await api.getNotas();
      const cnpjBase = empresaCnpj ?? empresaInfo?.cnpj;
      const normalizadas = (data || []).map((nota) => {
        const base = normalizeNotaFromApi(nota);
        if (!base.tipo_operacao && cnpjBase) {
          const tipo = inferTipoOperacao(base.emitente?.cnpj, base.destinatario?.cnpj, cnpjBase);
          if (tipo) {
            base.tipo_operacao = tipo;
          }
        }
        return base;
      });
      setNotas(normalizadas);
      return true;
    } catch (error) {
      console.error('Erro ao carregar notas:', error);
      if (!silent) {
        alert('Erro ao carregar notas fiscais');
      }
      setNotas([]);
      return false;
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const handleSubmitManual = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        numero: formData.numero,
        fornecedor:
          formData.tipo_operacao === 'pagar'
            ? formData.emitente_nome || formData.destinatario_nome
            : formData.destinatario_nome || formData.emitente_nome,
        valor_total: parseCurrencyString(formData.valor_total),
        data_emissao: formData.data_emissao,
        observacoes: formData.observacoes || '',
        tipo_operacao: formData.tipo_operacao,
        data_vencimento: formData.data_vencimento,
        parcelas: formParcelas,
        empresa_id: empresaInfo.id,
        metadata: {
          emitente: {
            nome: formData.emitente_nome,
            cnpj: formData.emitente_cnpj
          },
          destinatario: {
            nome: formData.destinatario_nome,
            cnpj: formData.destinatario_cnpj
          },
          parcelas: formParcelas,
          pagamentos: [],
          faturas: [],
          origem: 'manual'
        }
      };

      let permitirDuplicado = false;
      const duplicada = findDuplicateNota(notas, {
        numero: payload.numero,
        chave_acesso: payload.metadata?.chave_acesso
      });

      if (duplicada) {
        const confirmaDuplicata = confirm(
          `A nota fiscal ${payload.numero} já foi cadastrada. Deseja registrar novamente mesmo assim?`
        );
        if (!confirmaDuplicata) {
          return;
        }
        permitirDuplicado = true;
      }

      if (permitirDuplicado) {
        payload.permitir_duplicado = true;
        payload.allowDuplicate = true;
        payload.metadata = {
          ...payload.metadata,
          permitir_duplicado: true,
          allowDuplicate: true
        };
      }

      await api.createNota(payload);
      const sucesso = await loadNotas({ silent: true });
      if (!sucesso) {
        alert('Nota criada, mas houve erro ao recarregar a listagem. Atualize a página.');
      }

      setShowModal(false);
      setFormData(initialManualForm);
      setFormParcelas([]);
    } catch (error) {
      console.error('Erro ao salvar nota manual:', error);
      alert(error?.message || 'Erro ao salvar nota fiscal manualmente');
    }
  };

  const handleDelete = async (id) => {
    const nota = notas.find((item) => item.id === id || item.numero === id);
    if (!nota) return;

    const identificador = nota.numero ? `NF ${nota.numero}` : 'esta nota fiscal';
    const confirmou = confirm(`Tem certeza que deseja excluir ${identificador}?`);
    if (!confirmou) {
      return;
    }

    if (nota.id) {
      try {
        await api.deleteNota(nota.id);
      } catch (error) {
        console.error('Erro ao deletar nota no servidor:', error);
        alert('Não foi possível excluir esta nota fiscal. Tente novamente.');
        return;
      }
    }

    setNotas((prev) =>
      prev.filter((item) => {
        if (nota.id) {
          return item.id !== nota.id;
        }
        return item.numero !== nota.numero;
      })
    );
    if (selectedNotaId === (nota.id || nota.numero)) {
      setSelectedNotaId(null);
    }
    setSelectedNotaDirty(false);
    const sucessoRemocao = await loadNotas({ silent: true });
    if (!sucessoRemocao) {
      alert('Nota removida, mas houve erro ao recarregar a listagem. Atualize a página.');
    }
  };

  const handlePersistNota = async () => {
    if (!selectedNotaState) return;

    const notaAtual = selectedNotaState;

    if (!notaAtual.id) {
      alert('Não foi possível localizar esta nota no banco de dados. Atualize a página e tente novamente.');
      return;
    }

    if (!selectedNotaDirty) {
      return;
    }

    const payload = buildNotaPayloadForApi(notaAtual);
    if (!payload) return;
    payload.empresa_id = notaAtual.empresa_id || empresaInfo.id;

    const duplicada = findDuplicateNota(notas, payload, notaAtual.id);
    if (duplicada) {
      const confirma = confirm(
        `Já existe outra nota com o número ${payload.numero}. Deseja manter assim mesmo?`
      );
      if (!confirma) {
        return;
      }
    }

    try {
      setSavingNotaId(notaAtual.id);
      const resposta = await api.updateNota(notaAtual.id, payload);
      const { success: _success, ...notaSalva } = resposta || {};
      const notaComDados = {
        ...notaSalva,
        id: notaSalva.id ?? notaAtual.id,
        empresa_id: notaSalva.empresa_id ?? notaAtual.empresa_id ?? payload.empresa_id ?? null,
        numero: notaSalva.numero ?? payload.numero,
        fornecedor: notaSalva.fornecedor ?? notaAtual.fornecedor ?? '',
        data_emissao: notaSalva.data_emissao ?? payload.data_emissao,
        data_vencimento: notaSalva.data_vencimento ?? payload.data_vencimento,
        valor_total: notaSalva.valor_total ?? payload.valor_total,
        observacoes: notaSalva.observacoes ?? payload.observacoes,
        tipo_operacao: notaSalva.tipo_operacao ?? payload.tipo_operacao,
        origem: notaSalva.origem ?? payload.origem,
        status: notaSalva.status ?? payload.status,
        emitente: notaSalva.emitente ?? notaAtual.emitente,
        destinatario: notaSalva.destinatario ?? notaAtual.destinatario,
        chave_acesso: notaSalva.chave_acesso ?? payload.chave_acesso,
        parcelas: Array.isArray(notaSalva.parcelas) ? notaSalva.parcelas : payload.parcelas,
        pagamentos: Array.isArray(notaSalva.pagamentos) ? notaSalva.pagamentos : payload.pagamentos,
        faturas: Array.isArray(notaSalva.faturas) ? notaSalva.faturas : payload.faturas
      };

      setNotas((prev) =>
        prev.map((nota) => (nota.id === notaAtual.id ? { ...nota, ...notaComDados } : nota))
      );
      const snapshot = JSON.parse(JSON.stringify(notaComDados));
      setSelectedNotaState(snapshot);
      setSelectedNotaDirty(false);
      const sucessoAtualizacao = await loadNotas({ silent: true });
      if (!sucessoAtualizacao) {
        alert('Alterações salvas, mas houve erro ao recarregar a listagem. Atualize a página.');
      }
    } catch (error) {
      console.error('Erro ao atualizar nota fiscal:', error);
      alert('Não foi possível salvar as alterações desta nota fiscal.');
    } finally {
      setSavingNotaId(null);
    }
  };

  const handleGenerateLancamentos = async (nota) => {
    const parcelas = Array.isArray(nota.parcelas) && nota.parcelas.length > 0
      ? nota.parcelas
      : [
          {
            numero: 1,
            data_vencimento: nota.data_vencimento,
            valor: nota.valor_total
          }
        ];
  
    const parcelasInvalidas = parcelas.filter((parcela) => !parcela.data_vencimento || !parcela.valor);
    if (parcelasInvalidas.length > 0) {
      alert('Preencha data de vencimento e valor para todas as parcelas antes de gerar os lançamentos.');
      return;
    }
  
    if (!nota.tipo_operacao) {
      alert('Defina se a nota é uma conta a pagar ou a receber antes de gerar os lançamentos.');
      return;
    }
  
    setGeneratingLancamentoId(nota.id || nota.numero || 'local');
  
    try {
      // Determinar fornecedor ou cliente com base no tipo da nota
      const resolvedFornecedor =
        nota.emitente?.nome ||
        nota.emitente?.fantasia ||
        nota.fornecedor ||
        nota.destinatario?.nome ||
        nota.destinatario?.fantasia ||
        '';

      const resolvedCliente =
        nota.destinatario?.nome ||
        nota.destinatario?.fantasia ||
        nota.fornecedor ||
        nota.emitente?.nome ||
        nota.emitente?.fantasia ||
        '';

      const tipoDoc = nota.origem === 'xml_cte' ? 'CT-e' : 'NF';
      const fornecedor = nota.tipo_operacao === 'pagar' ? resolvedFornecedor || 'Fornecedor não identificado' : null;
      const cliente = nota.tipo_operacao === 'receber' ? resolvedCliente || 'Cliente não identificado' : null;
      const empresaEntidade = nota.tipo_operacao === 'pagar' ? fornecedor : cliente;
      const observacoesBase = `Gerado de ${tipoDoc} ${nota.numero || ''}${empresaEntidade ? ' - ' + empresaEntidade : ''}`;

      for (const parcela of parcelas) {
        const payload = {
          tipo: nota.tipo_operacao === 'pagar' ? 'despesa' : 'receita',
          descricao: `${tipoDoc} ${nota.numero || 's/n'} - Parcela ${parcela.numero}`,
          valor: parcela.valor,
          data_vencimento: parcela.data_vencimento,
          status: 'pendente',
          categoria: nota.origem === 'xml_cte' ? 'Transporte' : (nota.tipo_operacao === 'pagar' ? 'Fornecedores' : 'Clientes'),
          fornecedor: fornecedor,
          cliente: cliente,
          nota_fiscal_id: nota.id ?? null,
          nota_numero: nota.numero ?? null,
          observacoes: observacoesBase
        };

        try {
          await api.createLancamento(payload);
        } catch (error) {
          console.warn('Falha ao criar lançamento:', error);
        }
      }
      
      alert('Lançamentos gerados com sucesso!');
      
    } catch (error) {
      console.error('Erro ao gerar lançamentos:', error);
      alert('Erro ao gerar lançamentos para esta nota.');
    } finally {
      setGeneratingLancamentoId(null);
    }
  };

  const handleImportedFile = async (file) => {
    if (!file) return;
    setImportProcessing(true);
    setImportError('');
    try {
      const texto = await file.text();
      const notaProcessada = parseXmlNota(texto, empresaInfo?.cnpj);

      if (!notaProcessada) {
        throw new Error('Não foi possível interpretar o arquivo selecionado.');
      }

      if (!notaProcessada.tipo_operacao && empresaInfo?.cnpj) {
        const tipoInferido = inferTipoOperacao(
          notaProcessada.emitente?.cnpj,
          notaProcessada.destinatario?.cnpj,
          empresaInfo.cnpj
        );
        if (tipoInferido) {
          notaProcessada.tipo_operacao = tipoInferido;
        }
      }

      notaProcessada.arquivo_nome = file.name;
      setImportedNota(notaProcessada);
    } catch (error) {
      console.error('Erro ao importar nota:', error);
      setImportError(error.message || 'Não foi possível importar o arquivo selecionado.');
    } finally {
      setImportProcessing(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!importedNota) return;
    const payload = {
      numero: importedNota.numero,
      fornecedor:
        importedNota.tipo_operacao === 'pagar'
          ? importedNota.emitente?.nome || importedNota.emitente?.cnpj || ''
          : importedNota.destinatario?.nome || importedNota.destinatario?.cnpj || '',
      valor_total: importedNota.valor_total,
      data_emissao: importedNota.data_emissao,
      observacoes: importedNota.observacoes || '',
      tipo_operacao: importedNota.tipo_operacao,
      data_vencimento: importedNota.data_vencimento,
      parcelas: importedNota.parcelas,
      empresa_id: empresaInfo.id,
      metadata: buildMetadataFromNota(importedNota)
    };

    let permitirDuplicado = false;
    const duplicada = findDuplicateNota(notas, {
      numero: payload.numero,
      chave_acesso: payload.metadata?.chave_acesso
    });

    if (duplicada) {
      const confirmaDuplicata = confirm(
        `A nota fiscal ${payload.numero} já foi cadastrada. Deseja registrar novamente mesmo assim?`
      );
      if (!confirmaDuplicata) {
        return;
      }
      permitirDuplicado = true;
    }

    if (permitirDuplicado) {
      payload.permitir_duplicado = true;
      payload.allowDuplicate = true;
      payload.metadata = {
        ...payload.metadata,
        permitir_duplicado: true,
        allowDuplicate: true
      };
    }

    try {
      await api.createNota(payload);
      const sucesso = await loadNotas({ silent: true });
      if (!sucesso) {
        alert('Nota importada, mas houve erro ao recarregar a listagem. Atualize a página.');
      }
      setShowModal(false);
    } catch (error) {
      console.error('Erro ao salvar nota importada:', error);
      alert(error?.message || 'Erro ao salvar nota importada.');
      return;
    }
    setImportedNota(null);
  };

  const handleEmpresaChange = (campo, valor) => {
    setEmpresaInfo((prev) => ({ ...prev, [campo]: valor }));
    if (campo === 'cnpj' && importedNota && !importedNota.tipo_operacao) {
      const tipo = inferTipoOperacao(importedNota.emitente?.cnpj, importedNota.destinatario?.cnpj, valor);
      if (tipo) {
        setImportedNota((prev) => ({ ...prev, tipo_operacao: tipo }));
      }
    }
  };

  const closeDrawer = () => {
    setSelectedNotaId(null);
  };

  const atualizarNotaSelecionada = (updater) => {
    setSelectedNotaState((prev) => {
      if (!prev) return prev;
      const atualizado = typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
      setNotas((lista) =>
        lista.map((nota) =>
          nota.id === atualizado.id || nota.numero === atualizado.numero ? { ...nota, ...atualizado } : nota
        )
      );
      setSelectedNotaDirty(true);
      return { ...atualizado };
    });
  };

  const adicionarParcelaImportada = () => {
    setImportedNota((prev) => {
      if (!prev) return prev;
      const novaParcela = {
        numero: (prev.parcelas?.length || 0) + 1,
        data_vencimento: '',
        valor: 0
      };
      return {
        ...prev,
        parcelas: [...(prev.parcelas || []), novaParcela]
      };
    });
  };

  const removerParcelaImportada = (index) => {
    setImportedNota((prev) => {
      if (!prev) return prev;
      const novas = [...(prev.parcelas || [])];
      novas.splice(index, 1);
      return {
        ...prev,
        parcelas: novas
      };
    });
  };

  const adicionarParcelaManual = () => {
    setFormParcelas((prev) => [
      ...prev,
      {
        numero: prev.length + 1,
        data_vencimento: '',
        valor: ''
      }
    ]);
  };

  const atualizarParcelaManual = (index, campo, valor) => {
    setFormParcelas((prev) =>
      prev.map((parcela, idx) =>
        idx === index
          ? {
              ...parcela,
              [campo]: campo === 'valor' ? valor : valor
            }
          : parcela
      )
    );
  };

  const removerParcelaManual = (index) => {
    setFormParcelas((prev) => prev.filter((_, idx) => idx !== index));
  };

  const totalParcelasManual = formParcelas.reduce((acc, parcela) => acc + parseCurrencyString(parcela.valor), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="spinner h-12 w-12 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Carregando notas fiscais...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Notas Fiscais</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Importe e gerencie notas fiscais eletrônicas com geração automática de lançamentos
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => setShowModal(true)} className="btn-primary flex items-center gap-2">
            <Type className="w-5 h-5" />
            Cadastro manual
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4">
        <div className="card p-4 space-y-3">
          <div className="border border-dashed border-gray-300 dark:border-gray-700 rounded-2xl p-4 text-center">
            <div className="flex flex-col items-center gap-2">
              <Upload className="w-8 h-8 text-primary-500" />
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  Selecione o arquivo XML da NF-e
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  O sistema identifica emitente, destinatário, valores, parcelas e determina automaticamente o tipo de operação.
                </p>
              </div>
              <label className="btn-secondary cursor-pointer inline-flex items-center gap-2">
                <Upload className="w-4 h-4" />
                {importProcessing ? 'Processando...' : 'Escolher arquivo'}
                <input
                  type="file"
                  accept=".xml"
                  className="hidden"
                  disabled={importProcessing}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) {
                      handleImportedFile(file);
                      event.target.value = '';
                    }
                  }}
                />
              </label>
              {importError && (
                <div className="mt-2 text-sm text-rose-600 dark:text-rose-300 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {importError}
                </div>
              )}
            </div>
          </div>

          {importedNota && (
            <div className="border border-primary-200 dark:border-primary-800 bg-primary-50/60 dark:bg-primary-900/30 rounded-2xl p-5 space-y-5">
              <div className="flex justify-between items-start gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    {/* ✅ ADICIONE O ÍCONE */}
                    <span className="text-2xl">
                      {importedNota.origem === 'xml_cte' ? '🚚' : '📄'}
                    </span>
                    Pré-visualização {importedNota.origem === 'xml_cte' ? 'do CT-e' : 'da nota'} importada
                  </h3>

                  {/* ✅ ADICIONE O BADGE */}
                  {importedNota.origem === 'xml_cte' && (
          <span className="inline-block mt-2 px-3 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
            Conhecimento de Transporte Eletrônico
          </span>
        )}
        
        <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
          Revise os dados extraídos automaticamente antes de salvar.
        </p>
      </div>
                <button className="icon-button" onClick={() => setImportedNota(null)}>
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase font-semibold text-gray-500 dark:text-gray-400">Número</label>
                  <div className="flex items-center gap-2 mt-1">
                    <Hash className="w-4 h-4 text-primary-500" />
                    <input
                      type="text"
                      className="input-field"
                      value={importedNota.numero || ''}
                      onChange={(event) => setImportedNota((prev) => ({ ...prev, numero: event.target.value }))}
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase font-semibold text-gray-500 dark:text-gray-400">Data de emissão</label>
                  <div className="flex items-center gap-2 mt-1">
                    <Calendar className="w-4 h-4 text-primary-500" />
                    <input
                      type="date"
                      className="input-field"
                      value={importedNota.data_emissao || ''}
                      onChange={(event) => setImportedNota((prev) => ({ ...prev, data_emissao: event.target.value }))}
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase font-semibold text-gray-500 dark:text-gray-400">Valor total</label>
                  <div className="flex items-center gap-2 mt-1">
                    <DollarSign className="w-4 h-4 text-primary-500" />
                    <input
                      type="number"
                      step="0.01"
                      className="input-field"
                      value={importedNota.valor_total}
                      onChange={(event) =>
                        setImportedNota((prev) => ({
                          ...prev,
                          valor_total: parseFloat(event.target.value) || 0
                        }))
                      }
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase font-semibold text-gray-500 dark:text-gray-400">Tipo da operação</label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    <button
                      type="button"
                      onClick={() => setImportedNota((prev) => ({ ...prev, tipo_operacao: 'pagar' }))}
                      className={`px-3 py-2 rounded-lg border text-sm font-medium transition ${
                        importedNota.tipo_operacao === 'pagar'
                          ? 'border-rose-500 bg-rose-50 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200'
                          : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300'
                      }`}
                    >
                      Conta a pagar
                    </button>
                    <button
                      type="button"
                      onClick={() => setImportedNota((prev) => ({ ...prev, tipo_operacao: 'receber' }))}
                      className={`px-3 py-2 rounded-lg border text-sm font-medium transition ${
                        importedNota.tipo_operacao === 'receber'
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200'
                          : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300'
                      }`}
                    >
                      Conta a receber
                    </button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="card bg-white/80 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                    <Building2 className="w-4 h-4 text-primary-500" />
                    Emitente
                  </div>
                  <div className="space-y-2 text-sm">
                    <input
                      type="text"
                      className="input-field"
                      placeholder="Razão social do emitente"
                      value={importedNota.emitente?.nome || ''}
                      onChange={(event) =>
                        setImportedNota((prev) => ({
                          ...prev,
                          emitente: { ...prev.emitente, nome: event.target.value }
                        }))
                      }
                    />
                    <input
                      type="text"
                      className="input-field"
                      placeholder="CNPJ do emitente"
                      value={importedNota.emitente?.cnpj || ''}
                      onChange={(event) =>
                        setImportedNota((prev) => ({
                          ...prev,
                          emitente: { ...prev.emitente, cnpj: event.target.value }
                        }))
                      }
                    />
                  </div>
                </div>

                <div className="card bg-white/80 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                    <Building2 className="w-4 h-4 text-primary-500" />
                    Destinatário
                  </div>
                  <div className="space-y-2 text-sm">
                    <input
                      type="text"
                      className="input-field"
                      placeholder="Razão social do destinatário"
                      value={importedNota.destinatario?.nome || ''}
                      onChange={(event) =>
                        setImportedNota((prev) => ({
                          ...prev,
                          destinatario: { ...prev.destinatario, nome: event.target.value }
                        }))
                      }
                    />
                    <input
                      type="text"
                      className="input-field"
                      placeholder="CNPJ do destinatário"
                      value={importedNota.destinatario?.cnpj || ''}
                      onChange={(event) =>
                        setImportedNota((prev) => ({
                          ...prev,
                          destinatario: { ...prev.destinatario, cnpj: event.target.value }
                        }))
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <ClipboardList className="w-4 h-4 text-primary-500" />
                    Parcelas identificadas
                  </h4>
                  <button onClick={adicionarParcelaImportada} className="text-sm text-primary-600 dark:text-primary-300 flex items-center gap-1">
                    <Plus className="w-4 h-4" />
                    Adicionar parcela
                  </button>
                </div>
                {importedNota.parcelas && importedNota.parcelas.length > 0 ? (
                  <div className="space-y-3">
                    {importedNota.parcelas.map((parcela, index) => (
                      <div
                        key={`import-parcela-${index}`}
                        className="grid grid-cols-1 md:grid-cols-[auto,1fr,1fr,auto] gap-3 items-end bg-white dark:bg-gray-900/70 border border-gray-200 dark:border-gray-800 rounded-xl p-3"
                      >
                        <div>
                          <label className="text-xs uppercase font-semibold text-gray-500 dark:text-gray-400">#</label>
                          <input
                            type="text"
                            className="input-field"
                            value={parcela.numero}
                            onChange={(event) =>
                              setImportedNota((prev) => {
                                const parcelas = [...prev.parcelas];
                                parcelas[index] = { ...parcelas[index], numero: event.target.value };
                                return { ...prev, parcelas };
                              })
                            }
                          />
                        </div>
                        <div>
                          <label className="text-xs uppercase font-semibold text-gray-500 dark:text-gray-400">Vencimento</label>
                          <input
                            type="date"
                            className="input-field"
                            value={parcela.data_vencimento || ''}
                            onChange={(event) =>
                              setImportedNota((prev) => {
                                const parcelas = [...prev.parcelas];
                                parcelas[index] = { ...parcelas[index], data_vencimento: event.target.value };
                                return { ...prev, parcelas };
                              })
                            }
                          />
                        </div>
                        <div>
                          <label className="text-xs uppercase font-semibold text-gray-500 dark:text-gray-400">Valor</label>
                          <input
                            type="number"
                            step="0.01"
                            className="input-field"
                            value={parcela.valor}
                            onChange={(event) =>
                              setImportedNota((prev) => {
                                const parcelas = [...prev.parcelas];
                                parcelas[index] = {
                                  ...parcelas[index],
                                  valor: parseFloat(event.target.value) || 0
                                };
                                return { ...prev, parcelas };
                              })
                            }
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removerParcelaImportada(index)}
                          className="icon-button text-rose-500 hover:text-rose-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Nenhuma parcela encontrada. Informe um vencimento único abaixo para geração de lançamento.
                  </div>
                )}

                {(!importedNota.parcelas || importedNota.parcelas.length === 0) && (
                  <div>
                    <label className="text-xs uppercase font-semibold text-gray-500 dark:text-gray-400">Vencimento único</label>
                    <input
                      type="date"
                      className="input-field mt-1"
                      value={importedNota.data_vencimento || ''}
                      onChange={(event) =>
                        setImportedNota((prev) => ({ ...prev, data_vencimento: event.target.value }))
                      }
                    />
                  </div>
                )}
              </div>

              {(importedNota.faturas?.length || importedNota.pagamentos?.length) ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {importedNota.faturas?.length > 0 && (
                    <div className="card bg-white/90 dark:bg-gray-900/70 border border-gray-200 dark:border-gray-800">
                      <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                        <FileText className="w-4 h-4 text-primary-500" />
                        Faturas
                      </div>
                      <ul className="space-y-2 text-sm">
                        {importedNota.faturas.map((fatura, index) => (
                          <li key={`fatura-${index}`} className="flex flex-col gap-1">
                            <span className="font-semibold text-gray-800 dark:text-gray-100">{fatura.numero || `Fatura ${index + 1}`}</span>
                            <div className="flex flex-wrap gap-3 text-xs text-gray-600 dark:text-gray-400">
                              <span>Original: {formatCurrency(fatura.valor_original)}</span>
                              <span>Desconto: {formatCurrency(fatura.valor_desconto)}</span>
                              <span>Líquido: {formatCurrency(fatura.valor_liquido)}</span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {importedNota.pagamentos?.length > 0 && (
                    <div className="card bg-white/90 dark:bg-gray-900/70 border border-gray-200 dark:border-gray-800">
                      <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                        <CreditCard className="w-4 h-4 text-primary-500" />
                        Pagamentos identificados
                      </div>
                      <ul className="space-y-2 text-sm">
                        {importedNota.pagamentos.map((pagamento, index) => (
                          <li key={`pag-${index}`} className="flex flex-col gap-1">
                            <span className="font-semibold text-gray-800 dark:text-gray-100">{pagamento.meio || 'Forma de pagamento'}</span>
                            <div className="flex flex-wrap gap-3 text-xs text-gray-600 dark:text-gray-400">
                              <span>Valor: {formatCurrency(pagamento.valor)}</span>
                              {pagamento.indicador && <span>Indicador: {pagamento.indicador}</span>}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : null}

              <div className="flex flex-col sm:flex-row gap-3">
                <button onClick={handleConfirmImport} className="btn-primary flex-1">
                  Confirmar importação
                </button>
                <button onClick={() => setImportedNota(null)} className="btn-secondary flex-1">
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </div>

      </div>

      {notas.length > 0 && (
        <div className="card overflow-hidden">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Notas cadastradas</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Visualize emitente, status, parcelas identificadas e gere lançamentos financeiros de forma automática.
              </p>
            </div>
          </div>



          <div className="overflow-x-auto">
          <table className="min-w-full table-fixed divide-y divide-gray-200 dark:divide-gray-700">
          <colgroup>
  <col className="w-[80px]" />     {/* DOC */}
  <col className="w-[280px]" />    {/* Emitente / Dest. */}
  <col className="w-[90px]" />     {/* Número */}
  <col className="w-[110px]" />    {/* Emissão */}
  <col className="w-[120px]" />    {/* Valor Total */}
  <col className="w-[130px]" />    {/* Tipo */}
  <col className="w-[320px]" />    {/* Parcelas */}
  <col className="w-[140px]" />    {/* Ações */}
</colgroup>
          <thead className="bg-gray-50 dark:bg-gray-800/60">
                <tr>
                <th className="px-4 py-3 text-center whitespace-nowrap text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
  DOC
</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Emitente / Destinatário
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Número
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Emissão
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Valor Total
                  </th>
                  <th className="px-4 py-3 text-center whitespace-nowrap text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
  TIPO
</th>

                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Parcelas
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {notas.map((nota) => {
                  const parcelas = Array.isArray(nota.parcelas) ? nota.parcelas : [];
                  const tipoLabel = tipoOperacaoLabel(nota.tipo_operacao);
                  const nomePrincipal = nota.tipo_operacao === 'pagar'
                    ? nota.emitente?.nome || nota.emitente?.cnpj || nota.fornecedor
                    : nota.destinatario?.nome || nota.destinatario?.cnpj || nota.fornecedor;

                  return (
                    <tr
  key={nota.id || nota.numero}
  className="hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors"
>
  {/* ✅ COLUNA DOC SEM QUEBRA */}
  <td className="px-4 py-4 text-sm text-center whitespace-nowrap">
  <span
    className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
      (nota.origem === 'xml_cte')
        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200'
        : 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200'
    }`}
  >
    {nota.origem === 'xml_cte' ? <Truck size={14}/> : <FileText size={14}/>}
    {nota.origem === 'xml_cte' ? 'CT\u2011e' : 'NF\u2011e'}
  </span>
</td>

                      <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">
                        <button
                          onClick={() => setSelectedNotaId(nota.id || nota.numero)}
                          className="text-left space-y-1"
                        >
                          <div className="font-semibold">{nomePrincipal || 'Não informado'}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {nota.tipo_operacao === 'pagar' ? 'Fornecedor' : 'Cliente'} · NF {nota.numero || '—'}
                          </div>
                        </button>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-300">{nota.numero || '—'}</td>
                      <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-300">
                        {nota.data_emissao ? formatDate(nota.data_emissao) : '—'}
                      </td>
                      <td className="px-4 py-4 text-sm font-semibold text-primary-600 dark:text-primary-400">
                        {formatCurrency(nota.valor_total)}
                      </td>
                      <td className="px-4 py-4 text-sm whitespace-nowrap">
  <span
    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${getTipoOperacaoBadge(nota.tipo_operacao)}`}
  >
    {tipoLabel}
  </span>
</td>

<td className="px-4 py-4 align-top text-center">
  <ParcelasCell nota={nota} formatDate={formatDate} formatCurrency={formatCurrency} compact />
</td>

<td className="px-3 py-4 text-center">
  <div className="flex justify-center gap-1.5">
    {/* Ver detalhes */}
    <button
      onClick={() => setSelectedNotaId(nota.id || nota.numero)}
      className="p-2 hover:bg-blue-500/20 rounded-lg transition-colors text-blue-400"
      title="Ver detalhes"
    >
      <Eye size={18} />
    </button>

    {/* Editar (abre o drawer de detalhes para editar) */}
    <button
      onClick={() => setSelectedNotaId(nota.id || nota.numero)}
      className="p-2 hover:bg-yellow-500/20 rounded-lg transition-colors text-yellow-400"
      title="Editar"
    >
      <Edit size={18} />
    </button>

    {/* Gerar lançamentos */}
    <button
      onClick={() => handleGenerateLancamentos(nota)}
      disabled={generatingLancamentoId === (nota.id || nota.numero)}
      className="p-2 hover:bg-green-500/20 rounded-lg transition-colors text-green-400 disabled:opacity-50 disabled:pointer-events-none"
      title={generatingLancamentoId === (nota.id || nota.numero) ? 'Gerando lançamentos...' : 'Gerar lançamentos'}
      aria-busy={generatingLancamentoId === (nota.id || nota.numero)}
    >
     <Rocket size={18} />
    </button>

    {/* Excluir */}
    <button
      onClick={() => handleDelete(nota.id)}
      className="p-2 hover:bg-red-500/20 rounded-lg transition-colors text-red-400"
      title="Excluir"
    >
      <Trash2 size={18} />
    </button>
  </div>
</td>
</tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div
            className="w-full max-w-3xl bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-h-[90vh] flex flex-col"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Cadastro manual de nota fiscal</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Preencha os campos abaixo para registrar uma nota sem arquivo digital.
                </p>
              </div>
              <button className="icon-button" onClick={() => setShowModal(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <form className="p-6 space-y-6" onSubmit={handleSubmitManual}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Número da NF-e</label>
                    <input
                      type="text"
                      className="input-field"
                      value={formData.numero}
                      onChange={(event) => setFormData((prev) => ({ ...prev, numero: event.target.value }))}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Data de emissão</label>
                    <input
                      type="date"
                      className="input-field"
                      value={formData.data_emissao}
                      onChange={(event) => setFormData((prev) => ({ ...prev, data_emissao: event.target.value }))}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Valor total</label>
                    <input
                      type="number"
                      step="0.01"
                      className="input-field"
                      value={formData.valor_total}
                      onChange={(event) => setFormData((prev) => ({ ...prev, valor_total: event.target.value }))}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Tipo da operação</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, tipo_operacao: 'pagar' }))}
                        className={`px-3 py-2 rounded-lg border text-sm font-medium transition ${
                          formData.tipo_operacao === 'pagar'
                            ? 'border-rose-500 bg-rose-50 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200'
                            : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300'
                        }`}
                      >
                        Conta a pagar
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, tipo_operacao: 'receber' }))}
                        className={`px-3 py-2 rounded-lg border text-sm font-medium transition ${
                          formData.tipo_operacao === 'receber'
                            ? 'border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200'
                            : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300'
                        }`}
                      >
                        Conta a receber
                      </button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">Emitente</p>
                    <input
                      type="text"
                      className="input-field"
                      placeholder="Razão social"
                      value={formData.emitente_nome}
                      onChange={(event) => setFormData((prev) => ({ ...prev, emitente_nome: event.target.value }))}
                    />
                    <input
                      type="text"
                      className="input-field"
                      placeholder="CNPJ"
                      value={formData.emitente_cnpj}
                      onChange={(event) => setFormData((prev) => ({ ...prev, emitente_cnpj: event.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">Destinatário</p>
                    <input
                      type="text"
                      className="input-field"
                      placeholder="Razão social"
                      value={formData.destinatario_nome}
                      onChange={(event) => setFormData((prev) => ({ ...prev, destinatario_nome: event.target.value }))}
                    />
                    <input
                      type="text"
                      className="input-field"
                      placeholder="CNPJ"
                      value={formData.destinatario_cnpj}
                      onChange={(event) => setFormData((prev) => ({ ...prev, destinatario_cnpj: event.target.value }))}
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                      <ClipboardList className="w-4 h-4 text-primary-500" />
                      Parcelas
                    </h4>
                    <button type="button" onClick={adicionarParcelaManual} className="text-sm text-primary-600 dark:text-primary-300 flex items-center gap-1">
                      <Plus className="w-4 h-4" />
                      Adicionar parcela
                    </button>
                  </div>
                  {formParcelas.length > 0 ? (
                    <div className="space-y-3">
                      {formParcelas.map((parcela, index) => (
                        <div
                          key={`manual-parcela-${index}`}
                          className="grid grid-cols-1 md:grid-cols-[auto,1fr,1fr,auto] gap-3 items-end bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-800 rounded-xl p-3"
                        >
                          <div>
                            <label className="text-xs uppercase font-semibold text-gray-500 dark:text-gray-400">#</label>
                            <input
                              type="text"
                              className="input-field"
                              value={parcela.numero}
                              onChange={(event) => atualizarParcelaManual(index, 'numero', event.target.value)}
                            />
                          </div>
                          <div>
                            <label className="text-xs uppercase font-semibold text-gray-500 dark:text-gray-400">Vencimento</label>
                            <input
                              type="date"
                              className="input-field"
                              value={parcela.data_vencimento}
                              onChange={(event) => atualizarParcelaManual(index, 'data_vencimento', event.target.value)}
                            />
                          </div>
                          <div>
                            <label className="text-xs uppercase font-semibold text-gray-500 dark:text-gray-400">Valor</label>
                            <input
                              type="number"
                              step="0.01"
                              className="input-field"
                              value={parcela.valor}
                              onChange={(event) => atualizarParcelaManual(index, 'valor', event.target.value)}
                            />
                          </div>
                          <button type="button" onClick={() => removerParcelaManual(index)} className="icon-button text-rose-500 hover:text-rose-600">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Adicione parcelas ou preencha o vencimento único abaixo para gerar lançamento.
                    </p>
                  )}

                  {formParcelas.length === 0 && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Vencimento único</label>
                      <input
                        type="date"
                        className="input-field"
                        value={formData.data_vencimento}
                        onChange={(event) => setFormData((prev) => ({ ...prev, data_vencimento: event.target.value }))}
                      />
                    </div>
                  )}

                  {formParcelas.length > 0 && (
                    <div className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                      <Info className="w-4 h-4" />
                      Soma das parcelas: {formatCurrency(totalParcelasManual)}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Observações</label>
                  <textarea
                    rows="3"
                    className="input-field"
                    value={formData.observacoes}
                    onChange={(event) => setFormData((prev) => ({ ...prev, observacoes: event.target.value }))}
                    placeholder="Informações adicionais sobre a nota fiscal..."
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button type="submit" className="flex-1 btn-primary">
                    Salvar nota fiscal
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setFormData(initialManualForm);
                      setFormParcelas([]);
                    }}
                    className="flex-1 btn-secondary"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {selectedNotaState && (
        <div className="fixed inset-0 flex justify-end bg-black/40 z-40" onClick={closeDrawer}>
          <div
            className="w-full max-w-lg h-full bg-white dark:bg-gray-900 shadow-2xl animate-slide-in-right overflow-y-auto custom-scrollbar"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
  <div>
    <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
      Detalhes {selectedNotaState.origem === 'xml_cte' ? 'do CT-e' : 'da NF-e'}
    </p>
    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{selectedNotaState.numero}</h2>
                <div className="flex flex-wrap gap-2 mt-2">
                  <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${getTipoOperacaoBadge(selectedNotaState.tipo_operacao)}`}>
                    {tipoOperacaoLabel(selectedNotaState.tipo_operacao)}
                  </span>
                  <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                    {selectedNotaState.origem ? selectedNotaState.origem.toUpperCase() : 'MANUAL'}
                  </span>
                </div>
              </div>
              <button className="icon-button" onClick={closeDrawer}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="card bg-gray-50 dark:bg-gray-800/60">
                  <p className="text-xs uppercase text-gray-500 dark:text-gray-400 font-semibold mb-2">Emitente</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">{selectedNotaState.emitente?.nome || '—'}</p>
                  {selectedNotaState.emitente?.cnpj && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">CNPJ: {formatCnpj(selectedNotaState.emitente.cnpj)}</p>
                  )}
                </div>
                <div className="card bg-gray-50 dark:bg-gray-800/60">
                  <p className="text-xs uppercase text-gray-500 dark:text-gray-400 font-semibold mb-2">Destinatário</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">{selectedNotaState.destinatario?.nome || '—'}</p>
                  {selectedNotaState.destinatario?.cnpj && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">CNPJ: {formatCnpj(selectedNotaState.destinatario.cnpj)}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="card">
                  <p className="text-xs uppercase text-gray-500 dark:text-gray-400 font-semibold mb-2">Valor total</p>
                  <p className="text-xl font-bold text-primary-600 dark:text-primary-400">{formatCurrency(selectedNotaState.valor_total)}</p>
                </div>
                <div className="card">
                  <p className="text-xs uppercase text-gray-500 dark:text-gray-400 font-semibold mb-2">Data de emissão</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">
                    {selectedNotaState.data_emissao ? formatDate(selectedNotaState.data_emissao) : '—'}
                  </p>
                </div>
              </div>

              {selectedNotaState.chave_acesso && (
                <div className="card bg-gray-50 dark:bg-gray-800/60">
                  <p className="text-xs uppercase text-gray-500 dark:text-gray-400 font-semibold mb-2">Chave de acesso</p>
                  <p className="font-mono text-sm text-gray-700 dark:text-gray-300 break-all">{selectedNotaState.chave_acesso}</p>
                </div>
              )}

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Parcelas</h3>
                  <button
                    onClick={() =>
                      atualizarNotaSelecionada((nota) => ({
                        ...nota,
                        parcelas: [
                          ...(nota.parcelas || []),
                          {
                            numero: (nota.parcelas?.length || 0) + 1,
                            data_vencimento: '',
                            valor: 0
                          }
                        ]
                      }))
                    }
                    className="text-sm text-primary-600 dark:text-primary-300 flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" /> Adicionar parcela
                  </button>
                </div>

                {selectedNotaState.parcelas && selectedNotaState.parcelas.length > 0 ? (
                  <div className="space-y-3">
                    {selectedNotaState.parcelas.map((parcela, index) => (
                      <div
                        key={`drawer-parcela-${index}`}
                        className="grid grid-cols-1 sm:grid-cols-[auto,1fr,1fr,auto] gap-3 items-end bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-800 rounded-xl p-3"
                      >
                        <div>
                          <label className="text-xs uppercase text-gray-500 dark:text-gray-400 font-semibold">#</label>
                          <input
                            type="text"
                            className="input-field"
                            value={parcela.numero}
                            onChange={(event) =>
                              atualizarNotaSelecionada((nota) => {
                                const parcelas = [...(nota.parcelas || [])];
                                parcelas[index] = { ...parcelas[index], numero: event.target.value };
                                return { ...nota, parcelas };
                              })
                            }
                          />
                        </div>
                        <div>
                          <label className="text-xs uppercase text-gray-500 dark:text-gray-400 font-semibold">Vencimento</label>
                          <input
                            type="date"
                            className="input-field"
                            value={parcela.data_vencimento || ''}
                            onChange={(event) =>
                              atualizarNotaSelecionada((nota) => {
                                const parcelas = [...(nota.parcelas || [])];
                                parcelas[index] = { ...parcelas[index], data_vencimento: event.target.value };
                                return { ...nota, parcelas };
                              })
                            }
                          />
                        </div>
                        <div>
  <label className="text-xs uppercase text-gray-500 dark:text-gray-400 font-semibold">Valor</label>
  <input
    type="number"
    step="0.01"
    className="input-field w-full"
    style={{ minWidth: '120px' }}
    value={parcela.valor}
                            onChange={(event) =>
                              atualizarNotaSelecionada((nota) => {
                                const parcelas = [...(nota.parcelas || [])];
                                parcelas[index] = { ...parcelas[index], valor: parseFloat(event.target.value) || 0 };
                                return { ...nota, parcelas };
                              })
                            }
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            atualizarNotaSelecionada((nota) => {
                              const parcelas = [...(nota.parcelas || [])];
                              parcelas.splice(index, 1);
                              return { ...nota, parcelas };
                            })
                          }
                          className="icon-button text-rose-500 hover:text-rose-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Esta nota não possui parcelas registradas. Informe um vencimento único para gerar lançamentos.
                    </p>
                    <input
                      type="date"
                      className="input-field"
                      value={selectedNotaState.data_vencimento || ''}
                      onChange={(event) => atualizarNotaSelecionada({ data_vencimento: event.target.value })}
                    />
                  </div>
                )}
              </div>

              {selectedNotaState.faturas?.length > 0 && (
                <div className="card bg-gray-50 dark:bg-gray-900/50">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-primary-500" /> Faturas
                  </h3>
                  <ul className="space-y-2 text-sm">
                    {selectedNotaState.faturas.map((fatura, index) => (
                      <li key={`drawer-fatura-${index}`} className="flex flex-col gap-1">
                        <span className="font-semibold text-gray-800 dark:text-gray-100">{fatura.numero || `Fatura ${index + 1}`}</span>
                        <div className="flex flex-wrap gap-3 text-xs text-gray-600 dark:text-gray-400">
                          <span>Original: {formatCurrency(fatura.valor_original)}</span>
                          <span>Desconto: {formatCurrency(fatura.valor_desconto)}</span>
                          <span>Líquido: {formatCurrency(fatura.valor_liquido)}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedNotaState.pagamentos?.length > 0 && (
                <div className="card bg-gray-50 dark:bg-gray-900/50">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-primary-500" /> Pagamentos identificados
                  </h3>
                  <ul className="space-y-2 text-sm">
                    {selectedNotaState.pagamentos.map((pagamento, index) => (
                      <li key={`drawer-pagamento-${index}`} className="flex flex-col gap-1">
                        <span className="font-semibold text-gray-800 dark:text-gray-100">
  {getPaymentMethodName(pagamento.meio) || 'Forma de pagamento'}
</span>                        <div className="flex flex-wrap gap-3 text-xs text-gray-600 dark:text-gray-400">
                          <span>Valor: {formatCurrency(pagamento.valor)}</span>
                          {pagamento.indicador && <span>Indicador: {pagamento.indicador}</span>}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="space-y-3">
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Observações</label>
                <textarea
                  rows="4"
                  className="input-field"
                  value={selectedNotaState.observacoes || ''}
                  onChange={(event) => atualizarNotaSelecionada({ observacoes: event.target.value })}
                  placeholder="Anotações internas, pendências ou complementos desta nota."
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <button
                  onClick={handlePersistNota}
                  className={`btn-secondary flex-1 ${
                    canPersistSelectedNota || savingSelectedNota ? '' : 'opacity-60 cursor-not-allowed'
                  }`}
                  disabled={!canPersistSelectedNota}
                >
                  {selectedNotaState?.id
                    ? savingSelectedNota
                      ? 'Salvando...'
                      : selectedNotaDirty
                        ? 'Salvar Alterações'
                        : 'Alterações Salvas'
                    : selectedNotaDirty
                      ? 'Salvar localmente'
                      : 'Sem Alterações'}
                </button>
                <button
  onClick={() => handleGenerateLancamentos(selectedNotaState)}
  className="btn-primary flex-1 flex items-center justify-center gap-2"
>
  <Rocket size={30} />
  {generatingLancamentoId === (selectedNotaState.id || selectedNotaState.numero)
    ? 'Gerando Lançamento...'
    : 'Gerar Lançamento'}
</button>
                <button
                  onClick={() => handleDelete(selectedNotaState.id || selectedNotaState.numero)}
                  className="btn-danger flex-1"
                >
                  Excluir Nota
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
